import { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';
import {
  confirmBlofinLiveOrder, createBlofinLiveIntent, fetchBlofinLiveAccountStatus,
  fetchBlofinLiveInstruments, fetchBlofinLiveOrder, reconcileBlofinLiveOrder,
} from '../../api/manualTrading';
import BlofinLiveConfirmationDialog from './BlofinLiveConfirmationDialog';
import BlofinLiveReconcileDialog from './BlofinLiveReconcileDialog';
import {
  alignSignalLevels,
  alignStopLossLevel,
  normalizeDecimalInput,
  parseTelegramSignal,
} from './blofinTelegramSignal';

const STATUS_LABELS = {
  INTENT_RECORDED: 'Intention créée — non exécutée',
  SUBMITTING: 'Envoi en attente',
  FILL_UNKNOWN: 'Envoi ambigu — réconciliation nécessaire',
  ACKNOWLEDGED: 'Acquitté — fill non confirmé',
  PARTIALLY_FILLED: 'Exécution partielle',
  FILLED: 'Ordre exécuté',
  TP1_FILLED: 'TP1 exécuté',
  TP2_FILLED: 'TP2 exécuté',
  TP3_FILLED: 'TP3 exécuté',
  RUNNER_ACTIVE: 'Runner protégé actif',
  CLOSED: 'Position clôturée',
  POSITION_CLOSED: 'Position clôturée manuellement',
  REJECTED: 'Rejeté',
  DISPATCH_LOCKED: 'Dispatch verrouillé',
};

const TP_LABELS = {
  NOT_ARMED: 'TP non armés',
  ARMING: 'Armement des TP',
  ARMED: 'TP armés',
  PARTIAL: 'TP partiellement exécutés',
  TP1_FILLED: 'TP1 exécuté — SL en attente de confirmation',
  TP2_FILLED: 'TP2 exécuté — SL en attente de confirmation',
  TP3_FILLED: 'TP3 exécuté — SL en attente de confirmation',
  POSITION_CLOSED: 'Position clôturée',
  UNKNOWN: 'État TP indisponible — lecture requise',
  TP_MONITORING_DISABLED: 'TP bloqués : monitoring désactivé',
};

const SL_LEVEL_LABELS = {
  INITIAL: 'SL initial',
  BREAK_EVEN: 'SL Break-Even confirmé',
  BE: 'SL break-even brut',
  TP1: 'SL au prix TP1',
  TP2: 'SL runner au prix TP2',
};

const SL_AMEND_LABELS = {
  NONE: 'Aucun trailing demandé',
  PENDING: 'Modification du SL en cours',
  CONFIRMED: 'Trailing confirmé par relecture',
  REJECTED: 'Modification SL rejetée',
  UNCERTAIN: 'État SL incertain — vérification requise',
  FAILED: 'Modification SL échouée — ancien SL conservé',
  UNKNOWN: 'Modification SL ambiguë — vérification requise',
};

export function protectionStateLabel(state) {
  return SL_LABELS[state] || state;
}

export function slManagementAlert(data) {
  if (!data) return null;
  if (data.slState === 'UNKNOWN') {
    return { severity: 'warning', text: 'État du stop-loss indisponible. Vérifiez la protection réelle Bitunix avant toute action.' };
  }
  if (data.slAmendState === 'REJECTED') {
    return { severity: 'error', text: 'Modification du stop-loss rejetée par Bitunix. Le SL précédent est conservé ; une vérification est requise.' };
  }
  if (data.slAmendState === 'UNCERTAIN' || data.slAmendState === 'UNKNOWN') {
    return { severity: 'warning', text: 'État du stop-loss incertain. Vérifiez la protection réelle Bitunix avant toute action.' };
  }
  if (data.slAmendState === 'PENDING') {
    return { severity: 'info', text: 'Modification du stop-loss en cours, en attente de confirmation par relecture Bitunix.' };
  }
  if (data.slAmendState === 'CONFIRMED' && data.slLevel && data.slLevel !== 'INITIAL') {
    return { severity: 'success', text: 'Stop-loss confirmé par relecture Bitunix.' };
  }
  return null;
}

const SL_LABELS = {
  PENDING: 'SL en attente',
  PLACING: 'SL en cours de placement',
  CONFIRMED: 'Protection SL présente',
  PARTIAL: 'Protection partielle — reliquat ouvert',
  UNKNOWN: 'État SL indisponible — lecture requise',
  UNPROTECTED: 'UNPROTECTED',
  NOT_REQUIRED: 'SL non requis',
};

const LIVE_400_MESSAGES = {
  INSTRUMENT_UNKNOWN: 'Cet instrument n’existe pas dans le catalogue public BloFin Futures LIVE.',
  INSTRUMENT_NOT_TRADABLE: 'Cet instrument BloFin Futures n’est pas actuellement négociable (état non live).',
  INSTRUMENT_NOT_SWAP: 'Cet instrument n’est pas un contrat perpétuel (SWAP) BloFin Futures.',
  INSTRUMENT_QUOTE_NOT_USDT: 'Cet instrument n’est pas coté en USDT.',
  INSTRUMENT_NOT_CRYPTO: 'Cet instrument n’est pas un contrat crypto éligible au trading LIVE.',
  INSTRUMENT_NOT_LINEAR: 'Cet instrument n’est pas un contrat linéaire.',
  INSTRUMENT_SETTLE_NOT_USDT: 'Cet instrument n’est pas réglé en USDT.',
  POSITION_ALREADY_OPEN: 'Une position est déjà ouverte sur cet instrument. Clôturez-la avant de créer une intention LIVE.',
  POSITION_CHECK_UNAVAILABLE: 'La vérification de position est indisponible. Aucune intention n’a été créée.',
  STOP_LOSS_ON_WRONG_SIDE: 'Le stop-loss est du mauvais côté du prix de marché (LONG : sous le prix ; SHORT : au-dessus).',
  STOP_LOSS_OUT_OF_TICK: 'Le stop-loss n’est pas aligné sur le tick autorisé de l’instrument.',
  STOP_LOSS_REQUIRED: 'Un stop-loss strictement positif est requis.',
  TP_PRICES_REQUIRED: 'Les trois prix TP1, TP2 et TP3 sont requis ensemble.',
  TP_PRICE_INVALID: 'Les prix TP doivent être strictement positifs.',
  PROTECTION_ORDER_INVALID: 'Ordre invalide : LONG exige SL < entrée < TP1 < TP2 < TP3 ; SHORT exige l’inverse.',
  PRICE_INVALID: 'Un prix de protection est invalide.',
  PRICE_OUT_OF_TICK: 'Un prix TP ou SL n’est pas aligné sur le tick autorisé.',
  MARKET_REFERENCE_REQUIRED: 'Le prix de marché de référence est indisponible. Réessayez.',
  MARKET_REFERENCE_UNVERIFIED: 'Le prix de marché de référence n’a pas pu être vérifié. Réessayez.',
  MARKET_REFERENCE_STALE: 'Le prix de marché de référence est trop ancien. Réessayez.',
  INSTRUMENT_TICK_INVALID: 'Les contraintes de prix de l’instrument sont invalides.',
  INSTRUMENT_CONSTRAINTS_INVALID: 'Les contraintes de l’instrument sont invalides.',
  INSTRUMENT_NOT_AVAILABLE: 'L’instrument n’est pas disponible pour le trading LIVE.',
  INSTRUMENT_REQUIRED: 'Un instrument valide est requis.',
  CONTRACT_TYPE_UNSUPPORTED: 'L’instrument n’est pas un contrat linéaire.',
  SETTLE_CURRENCY_UNSUPPORTED: 'L’instrument n’est pas réglé en USDT.',
  QUANTITY_BELOW_MIN: 'La marge et le levier produisent une quantité inférieure au minimum de l’instrument.',
  QUANTITY_ROUNDED_TO_ZERO: 'La marge et le levier produisent une quantité nulle.',
  QUANTITY_ABOVE_MAX: 'La marge et le levier dépassent la quantité maximale de l’instrument.',
  NOTIONAL_INVALID: 'La marge et le levier doivent être strictement positifs.',
  BUDGET_INVALID: 'La marge et le levier doivent être strictement positifs.',
  BUDGET_OR_QUANTITY_INVALID: 'La marge, le levier et la quantité doivent être strictement positifs.',
  TP_ALLOCATION_INFEASIBLE: 'L’allocation TP n’est pas réalisable avec les contraintes de l’instrument.',
  TP_PARTIAL_ALLOCATION_UNSUPPORTED: 'L’allocation 3 TP (40/30/20) est impossible pour cette taille de position avec les minima de l’instrument. Augmentez la marge ou le levier, ou choisissez un instrument avec un minimum plus faible. Aucun ordre n’a été envoyé.',
  ACCOUNT_REQUIRED: 'Compte LIVE requis.',
  LIVE_ACCOUNT_REQUIRED: 'Compte LIVE requis.',
  DIRECTION_INVALID: 'Direction invalide.',
  DIRECTION_REQUIRED: 'Direction requise.',
  IDEMPOTENCY_KEY_INVALID: 'Clé d’idempotence invalide.',
  INTENT_EXPIRED: 'Cette intention a expiré. Préparez une nouvelle intention.',
  INTENT_SNAPSHOT_MISSING: 'Cette intention historique ne contient pas les contrôles de sécurité requis. Préparez une nouvelle intention.',
  REFERENCE_PRICE_DRIFT: 'Le prix a trop évolué depuis la préparation. Préparez une nouvelle intention.',
  SIZING_CHANGED: 'Le sizing a changé depuis la préparation. Préparez une nouvelle intention.',
  POSITION_MODE_UNVERIFIED: 'Le mode de position BloFin n’a pas pu être vérifié. Aucune exécution.',
  POSITION_MODE_UNSUPPORTED: 'Mode de position BloFin non supporté par ce moteur (NET requis).',
  LEVERAGE_MISMATCH: 'Le levier réel Bitunix diffère du levier de l’intention. Aucun ordre envoyé.',
  CAPABILITIES_UNAVAILABLE: 'Les capacités Bitunix du compte ne sont pas disponibles. Aucun ordre envoyé.',
  MARGIN_MODE_NOT_ISOLATION: 'Le compte Bitunix n’est pas en marge isolée. Aucun ordre envoyé.',
  POSITION_MODE_NOT_ONE_WAY: 'Le compte Bitunix n’est pas en mode ONE_WAY. Aucun ordre envoyé.',
  DIRECTION_UNSUPPORTED: 'La direction n’est pas supportée par cet instrument Bitunix.',
  MARKET_SYMBOL_MISMATCH: 'La réponse marché Bitunix ne correspond pas au symbole demandé.',
};

const DEFAULT_API = {
  fetchStatus: fetchBlofinLiveAccountStatus,
  fetchInstruments: fetchBlofinLiveInstruments,
  createIntent: createBlofinLiveIntent,
  fetchOrder: fetchBlofinLiveOrder,
  confirm: confirmBlofinLiveOrder,
  reconcile: reconcileBlofinLiveOrder,
};

const EXECUTED_STATUSES = new Set(['ACKNOWLEDGED', 'PARTIALLY_FILLED', 'FILLED']);
const DEFAULT_MARGIN_USDT = '10';
const DEFAULT_LEVERAGE = '5';
const PRICE_FIELDS = new Set(['stopLoss', 'tp1', 'tp2', 'tp3']);

export function confirmationOutcomeMessage(data) {
  if (data?.message === 'DISPATCH_LOCKED') {
    return 'Dispatch LIVE verrouille cote serveur. Aucun ordre MARKET n a ete envoye.';
  }
  if (data?.message === 'NOT_PENDING') {
    return 'Cette intention n est plus en attente de confirmation. Aucun ordre MARKET n a ete envoye.';
  }
  if (data?.message === 'ALREADY_CLAIMED') {
    return 'Cette intention est deja revendiquee ou en cours. Aucun second ordre MARKET n a ete envoye.';
  }
  if (data?.message === 'INTENT_EXPIRED') {
    return 'Cette intention a expire. Aucun ordre MARKET n a ete envoye. Preparez une nouvelle intention.';
  }
  if (data?.message === 'FEE_MODEL_UNKNOWN') {
    return 'Confirmation refusee : les frais du compte ne sont pas certifies. Aucun ordre MARKET n a ete envoye.';
  }
  if (data?.message === 'MARGIN_UNKNOWN') {
    return 'Confirmation refusee : la marge disponible est inconnue. Aucun ordre MARKET n a ete envoye.';
  }
  if (data?.message === 'FEE_BUDGET_BLOCKED') {
    return 'Confirmation refusee : le budget marge + frais + reserve est depasse. Aucun ordre MARKET n a ete envoye.';
  }
  if (data?.message === 'LEVERAGE_MISMATCH') {
    return 'Confirmation refusee : le levier configure sur Bitunix differe du levier de l intention. Aucun ordre MARKET n a ete envoye.';
  }
  if (data?.message === 'TP_PARTIAL_ALLOCATION_UNSUPPORTED_SL_CONFIRMED') {
    return 'Ordre MARKET execute et SL confirme, mais les trois TP partiels ne peuvent pas etre armes : '
      + 'taille de position insuffisante face au minimum de l instrument. Aucun TP n a ete fusionne. '
      + 'La position reste protegee par le SL ; verifiez la position et cloturez-la manuellement si necessaire.';
  }
  if (data?.status === 'REJECTED') {
    return `Ordre rejete. Aucune execution MARKET confirmee${data.lastError ? ` : ${data.lastError}` : '.'}`;
  }
  if (data?.status === 'FILL_UNKNOWN') {
    return 'Envoi ambigu : aucun fill confirme. Une reconciliation est necessaire.';
  }
  if (data?.message) {
    return `Confirmation refusee (${data.message}). Aucun ordre MARKET n a ete envoye.`;
  }
  return 'Aucun ordre MARKET n a ete confirme. La fenetre reste ouverte.';
}

function eligibleForSlReconcile(data) {
  return data?.status === 'FILLED' && data?.slState === 'UNPROTECTED';
}

function canReconcileProtection(data) {
  if (!data) return false;
  if (data.status === 'ACKNOWLEDGED' || data.status === 'FILL_UNKNOWN') return true;
  if (data.slState === 'UNPROTECTED') return true;
  return /_INSTALL_FAILED|PROTECTION_VERIFICATION_FAILED|AWAITING_FILL/.test(data.lastError || '');
}

function reconcileOutcome(result) {
  if (result?.slState === 'CONFIRMED') {
    return {
      severity: 'success',
      text: `SL distant confirme. tpslId=${result.slTpslId || 'inconnu'}. Aucun ordre MARKET n a ete envoye.`,
    };
  }
  if (result?.status === 'POSITION_CLOSED') {
    return {
      severity: 'warning',
      text: 'Position deja cloturee. Aucun SL et aucun TP n ont ete crees.',
    };
  }
  if (result?.slState === 'UNPROTECTED') {
    return {
      severity: 'error',
      text: `SL non confirme. L intention reste UNPROTECTED${result.lastError ? ` : ${result.lastError}` : '.'}`,
    };
  }
  return {
    severity: 'warning',
    text: `Reconciliation terminee : statut=${result?.status}, SL=${result?.slState}.`,
  };
}

function messageFor(error) {
  const status = error?.status;
  if (status === 403 && error?.reason) {
    return `Accès LIVE refusé (${error.reason}).`;
  }
  if (status === 400) {
    return LIVE_400_MESSAGES[error?.reason]
      || 'Requête LIVE invalide. Vérifiez le stop-loss (alignement et sens), la marge, le levier et l’absence de position ouverte sur l’instrument.';
  }
  if (status === 401 || status === 403) return 'Accès LIVE refusé.';
  if (status === 404) return 'Intention LIVE introuvable.';
  if (status === 409) return 'Conflit d’idempotence.';
  if (status === 503) return 'Données LIVE indisponibles, réessayez plus tard.';
  return 'Erreur LIVE inattendue.';
}

function positiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
}

function matchingOpenContracts(positions, instrumentId) {
  const target = String(instrumentId || '').trim().toUpperCase();
  if (!target || !Array.isArray(positions)) return 0;
  return positions.reduce((total, position) => {
    const positionInstrument = String(position?.instrumentId || '').trim().toUpperCase();
    if (positionInstrument !== target) return total;
    const contracts = Number(position?.contracts);
    return total + (Number.isFinite(contracts) ? Math.abs(contracts) : 0);
  }, 0);
}

function slDistance(entryPrice, stopLoss) {
  const entry = Number(entryPrice);
  const stop = Number(stopLoss);
  if (!Number.isFinite(entry) || !Number.isFinite(stop) || entry === 0) return null;
  return `${Math.abs((entry - stop) / entry * 100).toFixed(2)} %`;
}

function SignalSlSummary({ extraction, slAlignment, direction, stopLoss, manual, tickSize }) {
  if (!extraction) {
    return null;
  }
  const adjustment = direction === 'SHORT' ? '+5 %' : '-5 %';
  const tpRounds = ['tp1', 'tp2', 'tp3']
    .map((key, index) => ({ ...extraction.tpAlignment?.[key], label: ['TP1', 'TP2', 'TP3'][index] }))
    .filter((level) => level?.changed);

  return (
    <Alert severity={manual ? 'warning' : 'info'}>
      <Typography variant="body2">SL Telegram original : {extraction.slOriginal}</Typography>
      {manual ? (
        <Typography variant="body2">
          SL final : {stopLoss} (saisie manuelle, ajustement 5 % non réappliqué)
        </Typography>
      ) : (
        <>
          <Typography variant="body2">
            SL calculé {adjustment} : {slAlignment?.exactValue}
          </Typography>
          <Typography variant="body2">
            SL final aligné au tick {tickSize} : {stopLoss}
            {slAlignment?.changed ? ' (arrondi éloigné du prix d’entrée)' : ''}
          </Typography>
        </>
      )}
      {tpRounds.length > 0 && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          TP alignés au tick : {tpRounds.map((level) => (
            `${level.label} ${level.original} → ${level.value}`
          )).join(' · ')}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
        Tick instrument reçu du backend : {tickSize}. La validation LIVE reste l&apos;autorité finale.
      </Typography>
    </Alert>
  );
}

function tpQuantityLabel(armed, preview, percent) {
  if (armed !== undefined && armed !== null) {
    return `${armed} contrats armés (fill réel, cible ${percent} %)`;
  }
  if (preview !== undefined && preview !== null) {
    return `${preview} contrats prévisionnels (${percent} %)`;
  }
  return `quantité indisponible (${percent} %)`;
}

function LiveSummary({
  data, onReconcile, reconcileLabel = 'Reconcilier le SL', reconcileOnProtectionError = false,
}) {
  if (!data) return null;
  const rows = [
    ['ID d’intention', data.intentId],
    ['Statut', STATUS_LABELS[data.status] || data.status],
    ['Instrument', data.instrumentId],
    ['Direction', data.direction],
    ['Type', 'MARKET'],
    ['Marge USDT', data.marginUsdt],
    ['Levier', data.leverage],
    ['Notionnel', data.targetNotionalUsdt],
    ['Prix d’entrée estimé', data.entryPrice],
    ['Contrats demandés', data.requestedContracts],
    ['Stop-loss', data.stopLoss],
    ['Couverture SL', data.slWholePosition ? 'Position entière (size=-1)' : 'Quantité remplie'],
    ['Distance SL (indicatif)', slDistance(data.entryPrice, data.stopLoss)],
    ['Protection', protectionStateLabel(data.slState)],
    ['Palier SL', SL_LEVEL_LABELS[data.slLevel] || data.slLevel],
    ['Confirmation trailing', SL_AMEND_LABELS[data.slAmendState] || data.slAmendState],
    ['SL courant', data.slTriggerPrice],
    ['SL order ID', data.exchangeSlOrderId],
    ['TP1 order ID', data.exchangeTp1OrderId],
    ['TP2 order ID', data.exchangeTp2OrderId],
    ['TP3 order ID', data.exchangeTp3OrderId],
    ['Rempli', data.filledContracts],
    ['Prix moyen', data.averageFillPrice],
    ['État TP', TP_LABELS[data.tpState] || data.tpState],
    ['TP1', data.tp1Price == null ? null
      : `${data.tp1Price} — ${tpQuantityLabel(data.tp1Contracts, data.tp1PreviewContracts, 40)}`],
    ['TP2', data.tp2Price == null ? null
      : `${data.tp2Price} — ${tpQuantityLabel(data.tp2Contracts, data.tp2PreviewContracts, 30)}`],
    ['TP3', data.tp3Price == null ? null
      : `${data.tp3Price} — ${tpQuantityLabel(data.tp3Contracts, data.tp3PreviewContracts, 20)}`],
    ['Runner', data.runnerContracts != null
      ? `${data.runnerContracts} contrats armés (fill réel, cible 10 %)`
      : (data.runnerPreviewContracts == null ? null
        : `${data.runnerPreviewContracts} contrats prévisionnels (10 %)`)],
    ['TP1 rempli', data.tp1FilledContracts],
    ['TP2 rempli', data.tp2FilledContracts],
    ['TP3 rempli', data.tp3FilledContracts],
    ['Reste', data.remainingContracts],
    ['Frais TP1/TP2/TP3', [data.tp1Fee, data.tp2Fee, data.tp3Fee].some((v) => v != null)
      ? `${data.tp1Fee ?? 0} / ${data.tp2Fee ?? 0} / ${data.tp3Fee ?? 0}` : null],
  ].filter(([, value]) => value !== undefined && value !== null && value !== '');
  const slAlert = slManagementAlert(data);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">Intention LIVE</Typography>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={0.5}>
          {rows.map(([label, value]) => (
            <Box key={label} display="flex" justifyContent="space-between" gap={2}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="body2">{value}</Typography>
            </Box>
          ))}
        </Stack>
        {slAlert && (
          <Alert severity={slAlert.severity} sx={{ mt: 2 }}>{slAlert.text}</Alert>
        )}
        {onReconcile && reconcileOnProtectionError
          && (data.status === 'ACKNOWLEDGED' || data.status === 'FILL_UNKNOWN') && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Position potentiellement non protégée : l’entrée n’a pas encore de fill/protection confirmé.
            Une lecture fraîche puis une réconciliation explicite sont nécessaires.
            <Box sx={{ mt: 1 }}>
              <Button color="warning" variant="contained" size="small"
                onClick={() => onReconcile(data)}>{reconcileLabel}</Button>
            </Box>
          </Alert>
        )}
                {data.slState === 'UNPROTECTED' && (
          data.lastError === 'SL_ACTIVE_COVERAGE_UNVERIFIED' ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              SL actif identifié sur BloFin (tpslId {data.slTpslId || 'inconnu'}) — couverture non vérifiable.
              Aucun TP ne sera armé. Surveillance manuelle de la position requise.
              {onReconcile && eligibleForSlReconcile(data) && (
                <Box sx={{ mt: 1 }}>
                  <Button color="warning" variant="contained" size="small"
                    onClick={() => onReconcile(data)}>
                    {reconcileLabel}
                  </Button>
                </Box>
              )}
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mt: 2 }}>
              Position non protégée — SL non confirmé (UNPROTECTED).
              {onReconcile && eligibleForSlReconcile(data) && (
                <Box sx={{ mt: 1 }}>
                  <Button color="warning" variant="contained" size="small"
                    onClick={() => onReconcile(data)}>
                    {reconcileLabel}
                  </Button>
                </Box>
              )}
            </Alert>
          )
        )}
        {onReconcile && reconcileOnProtectionError && data.status === 'FILLED'
          && data.slState !== 'UNPROTECTED'
          && /_INSTALL_FAILED|PROTECTION_VERIFICATION_FAILED/.test(data.lastError || '') && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Protection incomplete : le SL est present mais un ou plusieurs TP ne sont pas confirmes.
            <Box sx={{ mt: 1 }}>
              <Button color="warning" variant="contained" size="small"
                onClick={() => onReconcile(data)}>{reconcileLabel}</Button>
            </Box>
          </Alert>
        )}
        {data.slState === 'PARTIAL' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Protection partielle : SL confirmé pour la quantité remplie, mais le reliquat de l’ordre
            peut encore s’exécuter. Protection complète non affirmée.
          </Alert>
        )}
        {data.slState !== 'UNPROTECTED' && data.protectionConfirmed === true && (
          <Alert severity="success" sx={{ mt: 2 }}>
            SL confirmé par relecture distante.
          </Alert>
        )}
        {data.slState !== 'UNPROTECTED' && data.slState !== 'PARTIAL'
          && data.protectionConfirmed !== true && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Protection non confirmée — aucune protection n’est affirmée.
          </Alert>
        )}
        {(data.lastError === 'TP_PARTIAL_ALLOCATION_UNSUPPORTED'
          || data.message === 'TP_PARTIAL_ALLOCATION_UNSUPPORTED_SL_CONFIRMED') && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            SL confirmé, mais les trois TP partiels ne sont pas armés : allocation 40/30/20
            impossible pour la quantité réellement remplie et le minimum de l’instrument.
            Aucun TP n’a été fusionné. La position reste protégée par le SL ; une action manuelle
            est requise.
          </Alert>
        )}
        {data.tpLastError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            TP/SL trailing : {data.tpLastError}
          </Alert>
        )}
        {data.tpPreviewStatus && data.tpPreviewStatus !== 'OK' && data.tp1Contracts == null && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Quantités TP prévisionnelles indisponibles : {data.tpPreviewError || data.tpPreviewStatus}.
            Aucune quantité n’est inventée.
          </Alert>
        )}
        {data.tp1Price != null && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Break-even brut : le SL TP1 est placé au prix moyen d’entrée sans compensation des frais.
            Les frais et le slippage peuvent produire une perte nette.
          </Alert>
        )}
        {data.lastError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Dernière erreur : {data.lastError}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function BlofinLiveOrderPanel({
  exchangeName = 'BloFin',
  api = DEFAULT_API,
  reconcileLabel = 'Reconcilier le SL',
  refreshBeforeReconcile = false,
  reconcileCreatesOrders = false,
  reconcileRequiresAcknowledgement = false,
} = {}) {
  const [identityRef, setIdentityRef] = useState('');
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [instruments, setInstruments] = useState([]);
  const [instrumentTickSizes, setInstrumentTickSizes] = useState({});
  const [instrumentsError, setInstrumentsError] = useState('');
  const [loading, setLoading] = useState('');
  const [form, setForm] = useState({
    instrumentId: '', direction: 'LONG', marginUsdt: DEFAULT_MARGIN_USDT,
    leverage: DEFAULT_LEVERAGE, stopLoss: '', tp1: '', tp2: '', tp3: '',
  });
  const [signalText, setSignalText] = useState('');
  const [signalError, setSignalError] = useState('');
  const [signalExtraction, setSignalExtraction] = useState(null);
  const [slManuallyEdited, setSlManuallyEdited] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [intent, setIntent] = useState(null);
  const [intentError, setIntentError] = useState('');
  const [lookupId, setLookupId] = useState('');
  const [lookup, setLookup] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmationError, setConfirmationError] = useState('');
  const [reconcileTarget, setReconcileTarget] = useState(null);
  const [reconcilePreparing, setReconcilePreparing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileError, setReconcileError] = useState('');
  const [reconcileResult, setReconcileResult] = useState(null);

  const authorized = status?.authorized === true;
  const readOnly = status?.readOnlyAvailable === true;
  const dispatch = status?.dispatchEnabled === true;
  const canPrepare = authorized && readOnly;
  const selectedInstrument = form.instrumentId.trim();
  const selectedTickSize = instrumentTickSizes[form.instrumentId] || '';
  const signalSlAlignment = signalExtraction && selectedTickSize
    ? alignStopLossLevel(signalExtraction.slOriginal, form.direction, selectedTickSize)
    : null;
  const openContracts = matchingOpenContracts(status?.positions, selectedInstrument);
  const hasOpenPosition = openContracts > 0;
  const canSubmit = canPrepare && identityRef.trim() && selectedInstrument
    && positiveNumber(form.marginUsdt) && positiveNumber(form.leverage)
    && positiveNumber(form.stopLoss)
    && positiveNumber(form.tp1) && positiveNumber(form.tp2) && positiveNumber(form.tp3)
    && instruments.includes(form.instrumentId)
    && !hasOpenPosition && loading === '';

  function updateForm(field, value) {
    const normalized = PRICE_FIELDS.has(field) ? normalizeDecimalInput(value) : value;
    setForm((current) => {
      const next = { ...current, [field]: normalized };
      if (field === 'direction' && signalExtraction && !slManuallyEdited) {
        const aligned = alignStopLossLevel(
          signalExtraction.slOriginal,
          value,
          selectedTickSize,
        );
        if (aligned) {
          next.stopLoss = aligned.value;
        }
      }
      return next;
    });
    if (field === 'stopLoss' && signalExtraction) {
      setSlManuallyEdited(normalized !== (signalSlAlignment?.value || ''));
    }
    setIntent(null);
    setIntentError('');
    setConfirmationOpen(false);
    setConfirmationError('');
    setIdempotencyKey(crypto.randomUUID());
  }

  function onExtractSignal() {
    const tickSize = instrumentTickSizes[form.instrumentId];
    if (!form.instrumentId || !tickSize) {
      setSignalError(
        `Métadonnées tickSize indisponibles pour ${form.instrumentId || 'cet instrument'}. `
        + 'Actualisez le statut du compte avant toute extraction.',
      );
      return;
    }
    const parsed = parseTelegramSignal(signalText);
    if (!parsed.ok) {
      setSignalError(parsed.error);
      return;
    }
    const extraction = {
      slOriginal: parsed.levels.sl,
      tp1Original: parsed.levels.tp1,
      tp2Original: parsed.levels.tp2,
      tp3Original: parsed.levels.tp3,
    };
    const alignment = alignSignalLevels(extraction, form.direction, tickSize);
    if (!alignment) {
      setSignalError('Impossible de calculer les niveaux alignés au tick de l’instrument.');
      return;
    }
    extraction.tpAlignment = { tp1: alignment.tp1, tp2: alignment.tp2, tp3: alignment.tp3 };
    setSignalExtraction(extraction);
    setSlManuallyEdited(false);
    setSignalError('');
    setForm((current) => ({
      ...current,
      tp1: alignment.tp1.value,
      tp2: alignment.tp2.value,
      tp3: alignment.tp3.value,
      stopLoss: alignment.sl.value,
    }));
    setIntent(null);
    setIntentError('');
    setConfirmationOpen(false);
    setConfirmationError('');
    setIdempotencyKey(crypto.randomUUID());
  }

  async function refreshStatus() {
    const ref = identityRef.trim();
    if (!ref) {
      setStatusError('Saisissez l’identité technique du compte LIVE.');
      return;
    }
    setLoading('status');
    setStatusError('');
    setInstrumentsError('');
    try {
      const nextStatus = await api.fetchStatus(ref);
      setStatus(nextStatus);
      if (nextStatus?.authorized === true && nextStatus?.readOnlyAvailable === true) {
        setLoading('instruments');
        try {
          const catalog = await api.fetchInstruments(ref);
          const list = Array.isArray(catalog?.instruments) ? catalog.instruments : [];
          setInstruments(list);
          setInstrumentTickSizes(
            catalog?.tickSizes && typeof catalog.tickSizes === 'object'
              ? catalog.tickSizes
              : {},
          );
          setForm((current) => {
            if (list.includes(current.instrumentId)) return current;
            const preferred = list.includes('BTC-USDT') ? 'BTC-USDT' : (list[0] || '');
            return { ...current, instrumentId: preferred };
          });
        } catch (e) {
          setInstruments([]);
          setInstrumentTickSizes({});
          setInstrumentsError(messageFor(e));
        }
      } else {
        setInstruments([]);
        setInstrumentTickSizes({});
      }
    } catch (e) {
      setStatus(null);
      setInstruments([]);
      setInstrumentTickSizes({});
      setStatusError(messageFor(e));
    } finally {
      setLoading('');
    }
  }

  async function onCreateIntent() {
    if (!canSubmit) return;
    setLoading('intent');
    setIntentError('');
    try {
      setIntent(await api.createIntent({
        idempotencyKey,
        liveAccountIdentityRef: identityRef.trim(),
        instrumentId: selectedInstrument,
        direction: form.direction,
        marginUsdt: Number(form.marginUsdt),
        leverage: Number(form.leverage),
        stopLossPrice: Number(form.stopLoss),
        tp1Price: Number(form.tp1),
        tp2Price: Number(form.tp2),
        tp3Price: Number(form.tp3),
      }));
    } catch (e) {
      setIntentError(messageFor(e));
    } finally {
      setLoading('');
    }
  }

  async function onLookup() {
    if (!lookupId.trim() || !identityRef.trim()) return;
    setLoading('lookup');
    setLookupError('');
    setReconcileResult(null);
    try {
      setLookup(await api.fetchOrder(lookupId.trim(), identityRef.trim()));
    } catch (e) {
      setLookup(null);
      setLookupError(messageFor(e));
    } finally {
      setLoading('');
    }
  }

  async function onConfirmLive() {
    if (!intent || !dispatch || intent.status !== 'INTENT_RECORDED') return;
    setConfirming(true);
    setConfirmationError('');
    try {
      const updated = await api.confirm(intent.intentId, {
        liveAccountIdentityRef: identityRef.trim(),
        confirmed: true,
      });
      setIntent(updated);
      const executed = updated?.dispatchAttempted === true
        && EXECUTED_STATUSES.has(updated?.status);
      if (executed) {
        setConfirmationOpen(false);
      } else {
        setConfirmationError(confirmationOutcomeMessage(updated));
      }
    } catch (e) {
      setConfirmationError(messageFor(e));
    } finally {
      setConfirming(false);
    }
  }

  async function openReconcile(data) {
    if (!canReconcileProtection(data)) return;
    setReconcileTarget(null);
    setReconcileError('');
    setReconcileResult(null);
    if (!refreshBeforeReconcile || reconcilePreparing) {
      setReconcileTarget(data);
      return;
    }
    setReconcilePreparing(true);
    try {
      const refreshed = await api.fetchOrder(data.intentId, identityRef.trim());
      setReconcileTarget(refreshed);
    } catch (e) {
      setReconcileError(messageFor(e));
    } finally {
      setReconcilePreparing(false);
    }
  }

  async function onReconcileLive() {
    if (!reconcileTarget || reconciling) return;
    setReconciling(true);
    setReconcileError('');
    try {
      const updated = await api.reconcile(reconcileTarget.intentId, {
        liveAccountIdentityRef: identityRef.trim(),
      });
      setReconcileResult(updated);
      setReconcileTarget(null);
      setIntent((current) => (current?.intentId === updated?.intentId ? updated : current));
      setLookup((current) => (current?.intentId === updated?.intentId ? updated : current));
    } catch (e) {
      setReconcileError(messageFor(e));
    } finally {
      setReconciling(false);
    }
  }

  const reconcileOutcomeView = reconcileResult ? reconcileOutcome(reconcileResult) : null;

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Compte {exchangeName} LIVE</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" fullWidth label="Compte LIVE (identité technique)"
              value={identityRef} onChange={(e) => {
                setIdentityRef(e.target.value);
                setStatus(null);
                setStatusError('');
                setInstruments([]);
                setInstrumentTickSizes({});
                setInstrumentsError('');
                setIntent(null);
                setIntentError('');
                setConfirmationOpen(false);
                setConfirmationError('');
                setReconcileTarget(null);
                setReconcileError('');
                setReconcileResult(null);
              }} />
            <Button onClick={refreshStatus} disabled={loading !== '' || !identityRef.trim()}>
              {loading === 'status' ? <CircularProgress size={18} /> : 'Actualiser le statut'}
            </Button>
          </Stack>
          {statusError && <Alert severity="error" sx={{ mt: 2 }}>{statusError}</Alert>}
          {status && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip color={authorized ? 'success' : 'default'}
                  label={authorized ? 'Compte autorisé' : 'Compte non autorisé'} />
                <Chip color={readOnly ? 'success' : 'warning'}
                  label={readOnly ? 'Lecture seule disponible' : 'Lecture seule indisponible'} />
                <Chip color={dispatch ? 'error' : 'success'}
                  label={dispatch ? 'Dispatch activé' : 'Dispatch verrouillé'} />
              </Stack>
              <Typography variant="body2">Permissions API : {status.permissions}</Typography>
              <Typography variant="body2">Type de clé : {status.keyType}</Typography>
              <Typography variant="body2">Positions ouvertes : {status.openPositionsCount}</Typography>
              {(status.positions || []).map((position) => (
                <Typography key={`${position.instrumentId}-${position.positionSide}`} variant="body2">
                  {position.instrumentId} {position.positionSide} — {position.contracts} contrats
                </Typography>
              ))}
              {status.reason && status.reason !== 'NONE' && (
                <Alert severity="warning">{status.reason}</Alert>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Préparer une intention LIVE</Typography>
          <Stack spacing={2}>
            <TextField select label="Instrument" value={form.instrumentId}
              onChange={(e) => updateForm('instrumentId', e.target.value)} fullWidth
              disabled={!canPrepare || instruments.length === 0}
              helperText={instruments.length === 0
                ? 'Catalogue LIVE indisponible : actualisez le statut du compte.'
                : `Seuls les instruments du catalogue public ${exchangeName} Futures LIVE sont sélectionnables.`}>
              {instruments.map((instrumentId) => (
                <MenuItem key={instrumentId} value={instrumentId}>{instrumentId}</MenuItem>
              ))}
            </TextField>
            {instrumentsError && <Alert severity="error">{instrumentsError}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Direction" value={form.direction}
                onChange={(e) => updateForm('direction', e.target.value)} fullWidth>
                <MenuItem value="LONG">LONG</MenuItem>
                <MenuItem value="SHORT">SHORT</MenuItem>
              </TextField>
              <TextField label="Type (LIVE)" value="MARKET" disabled fullWidth
                helperText="Seul MARKET est supporté par le backend LIVE actuel." />
            </Stack>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Saisie rapide depuis Telegram</Typography>
              <TextField
                label="Coller un signal Telegram"
                value={signalText}
                onChange={(e) => {
                  setSignalText(e.target.value);
                  if (signalError) setSignalError('');
                }}
                multiline
                minRows={3}
                fullWidth
                placeholder={'TP1 : 0.396165\nTP2 : 0.41522\nTP3 : 0.435981\nSL : 0.35335'}
              />
              <Button
                variant="outlined"
                onClick={onExtractSignal}
                disabled={!signalText.trim() || loading !== ''}
                sx={{ mt: 1 }}
              >
                Extraire les niveaux
              </Button>
              {signalError && <Alert severity="error" sx={{ mt: 1 }}>{signalError}</Alert>}
              <Box sx={{ mt: 1 }}>
                <SignalSlSummary
                  extraction={signalExtraction}
                  slAlignment={signalSlAlignment}
                  direction={form.direction}
                  stopLoss={form.stopLoss}
                  manual={slManuallyEdited}
                  tickSize={selectedTickSize}
                />
              </Box>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Marge USDT" type="number" value={form.marginUsdt}
                onChange={(e) => updateForm('marginUsdt', e.target.value)} fullWidth />
              <TextField label="Levier" type="number" value={form.leverage}
                onChange={(e) => updateForm('leverage', e.target.value)} fullWidth />
            </Stack>
            <TextField label="Stop-loss" type="text" inputProps={{ inputMode: 'decimal' }}
              value={form.stopLoss} onChange={(e) => updateForm('stopLoss', e.target.value)}
              fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="TP1" type="text" inputProps={{ inputMode: 'decimal' }}
                value={form.tp1} onChange={(e) => updateForm('tp1', e.target.value)} fullWidth
                helperText="40 % — premier palier" />
              <TextField label="TP2" type="text" inputProps={{ inputMode: 'decimal' }}
                value={form.tp2} onChange={(e) => updateForm('tp2', e.target.value)} fullWidth
                helperText="30 % — SL déplacé au TP1" />
              <TextField label="TP3" type="text" inputProps={{ inputMode: 'decimal' }}
                value={form.tp3} onChange={(e) => updateForm('tp3', e.target.value)} fullWidth
                helperText="20 % — SL runner au TP2" />
            </Stack>
            <Alert severity="info">
              Allocation : TP1 40 %, TP2 30 %, TP3 20 %, Runner 10 %. Les TP sont des ordres de
              sortie reduce-only ; le runner reste ouvert avec le SL au prix TP2.
            </Alert>
            <Alert severity="warning">
              Break-even brut : le SL TP1 est placé au prix moyen d’entrée, sans compensation des
              frais. Les frais et le slippage peuvent produire une perte nette.
            </Alert>
            {!canPrepare && (
              <Alert severity="warning">
                La création d’intention nécessite un compte LIVE autorisé et la lecture seule
                disponible. Aucun envoi n’est effectué par cette page.
              </Alert>
            )}
            {hasOpenPosition && (
              <Alert severity="warning">
                Position ouverte détectée sur {selectedInstrument} ({openContracts} contrats) —
                clôturez-la avant de créer une intention LIVE.
              </Alert>
            )}
            <Button variant="contained" onClick={onCreateIntent} disabled={!canSubmit}>
              {loading === 'intent' ? <CircularProgress size={18} /> : 'Créer l’intention (aucun envoi)'}
            </Button>

            <Divider />
            <Button
              variant="outlined"
              disabled={!intent || intent.status !== 'INTENT_RECORDED'}
              onClick={() => { setConfirmationError(''); setConfirmationOpen(true); }}
            >
              Verifier et preparer la confirmation
            </Button>
            <Typography variant="caption" color="text.secondary">
              Etape 1 : verification du recapitulatif. Etape 2 : confirmation explicite dans la boite de dialogue.
              Le backend refuse toute execution si le dispatch est desactive ou si une condition de securite n'est pas remplie.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {intentError && <Alert severity="error">{intentError}</Alert>}
      {reconcileOutcomeView && (
        <Alert severity={reconcileOutcomeView.severity}>{reconcileOutcomeView.text}</Alert>
      )}
      {intent && <LiveSummary data={intent} onReconcile={openReconcile} reconcileLabel={reconcileLabel}
        reconcileOnProtectionError={reconcileCreatesOrders} />}

      <BlofinLiveConfirmationDialog
        open={confirmationOpen && Boolean(intent)}
        intent={intent}
        accountRef={identityRef.trim()}
        dispatchEnabled={dispatch}
        dispatchReason={status?.dispatchReason}
        busy={confirming}
        error={confirmationError}
        onClose={() => {
          if (!confirming) {
            setConfirmationOpen(false);
            setConfirmationError('');
          }
        }}
        onConfirm={onConfirmLive}
      />

      <BlofinLiveReconcileDialog
        open={Boolean(reconcileTarget)}
        intent={reconcileTarget}
        busy={reconciling || reconcilePreparing}
        exchangeName={exchangeName}
        createsProtectionOrders={reconcileCreatesOrders}
        requiresAcknowledgement={reconcileRequiresAcknowledgement}
        error={reconcileError}
        onClose={() => {
          if (!reconciling) {
            setReconcileTarget(null);
            setReconcileError('');
          }
        }}
        onConfirm={onReconcileLive}
      />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Consulter une intention LIVE</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" fullWidth label="intentId" value={lookupId}
              onChange={(e) => setLookupId(e.target.value)} />
            <Button onClick={onLookup}
              disabled={loading !== '' || !lookupId.trim() || !identityRef.trim()}>
              {loading === 'lookup' ? <CircularProgress size={18} /> : 'Consulter'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      {lookupError && <Alert severity="error">{lookupError}</Alert>}
      {lookup && <LiveSummary data={lookup} onReconcile={openReconcile} reconcileLabel={reconcileLabel}
        reconcileOnProtectionError={reconcileCreatesOrders} />}
    </Stack>
  );
}
